import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useConnection } from 'wagmi';

import { DASH, MZUSD_DECIMALS, TRANCHE_SHARE_DECIMALS } from '@/app/shared/constants/common';
import { Interval } from '@/app/shared/constants/interval';
import { HISTORY_ACTION_LABELS } from '@/app/shared/constants/portfolio';
import { QueryKeys } from '@/app/shared/constants/queryKeys';
import { formatFee, formatHistoryDate, formatTokenAmount, formatUsd } from '@/app/shared/lib/formatters';
import { resolveTrancheDisplayLabel } from '@/app/shared/lib/tranche';
import { getUserTransactions } from '@/app/shared/services/api';
import { UserTransaction } from '@/app/shared/services/api/types';

export type HistoryTab = 'all' | 'deposits' | 'withdrawals';
export type HistoryKind = 'deposit' | 'withdrawal';

export interface PortfolioHistoryRow {
  id: string;
  action: string;
  vault: string;
  amount: string;
  amountUsd: string;
  share: string;
  fee: string;
  date: string;
  kind: HistoryKind;
  txHash: string;
}

const resolveKind = (actionType: string): HistoryKind => {
  const normalized = actionType.toLowerCase();
  if (normalized.includes('deposit')) return 'deposit';
  return 'withdrawal';
};

const mapTransactionToRow = (tx: UserTransaction, index: number): PortfolioHistoryRow => {
  const symbol = tx.trancheSymbol || 'mzUSD';
  const amountValue = formatTokenAmount(tx.amount, MZUSD_DECIMALS);
  const shareValue = formatTokenAmount(tx.shares, TRANCHE_SHARE_DECIMALS);

  return {
    id: tx.txHash || `${tx.trancheAddress}-${tx.timestamp}-${index}`,
    action: HISTORY_ACTION_LABELS[tx.actionType] ?? tx.actionType,
    vault: `${tx.vaultName || DASH} · ${resolveTrancheDisplayLabel(tx.trancheName, tx.trancheSymbol)}`,
    amount: amountValue === DASH ? DASH : `${amountValue} ${symbol}`,
    amountUsd: formatUsd(tx.amountUsd),
    share: shareValue === DASH || shareValue === '0.00' ? 'N/A' : shareValue,
    fee: formatFee(tx.fee),
    date: formatHistoryDate(tx.timestamp),
    kind: resolveKind(tx.actionType),
    txHash: tx.txHash,
  };
};

export const usePortfolioHistory = (activeTab: HistoryTab = 'all') => {
  const { address, chain } = useConnection();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [QueryKeys.USER_TRANSACTIONS, address],
    queryFn: () => getUserTransactions(address),
    enabled: !!address,
    staleTime: Interval.ONE_MIN,
    refetchInterval: Interval.ONE_MIN,
  });

  const allRows = useMemo(
    () => (data?.transactions ?? []).map((tx, index) => mapTransactionToRow(tx, index)),
    [data?.transactions],
  );

  const rows = useMemo(() => {
    if (activeTab === 'all') return allRows;
    const kind = activeTab === 'deposits' ? 'deposit' : 'withdrawal';
    return allRows.filter((row) => row.kind === kind);
  }, [activeTab, allRows]);

  const explorerBase = chain?.blockExplorers?.default?.url ?? 'https://etherscan.io';

  return {
    rows,
    count: data?.count ?? allRows.length,
    filteredCount: rows.length,
    isLoading,
    error,
    refetch,
    isConnected: !!address,
    getTxUrl: (txHash: string) => `${explorerBase}/tx/${txHash}`,
  };
};
